import React, { useState, useEffect } from 'react';
import { useApiContext } from '../context/ApiContext';
import ApartmentSearchForm from '../components/ApartmentSearchForm';
import ApartmentSearchResults from '../components/ApartmentSearchResults';

export default function ApartmentSearch() {
  const { api } = useApiContext();
  
  // 필터 상태
  const [filters, setFilters] = useState({
    region: '',
    districts: [],
    subdistricts: [],  // 단수에서 복수로 변경
    households: { min: null, max: null },
    area: { min: null, max: null },
    price: { min: null, max: null },
    subwayTime: null,
    priceChange: '',
    sortBy: 'sale_price',
    sortOrder: 'desc',
    limit: 10
  });
  
  // 결과 상태
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 지역 데이터 상태
  const [regionData, setRegionData] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [subdistrictData, setSubdistrictData] = useState([]);
  
  // 지역 데이터 로드
  useEffect(() => {
    loadRegionData();
  }, []);
  
  // 지역별 하위 데이터 로드
  useEffect(() => {
    if (filters.region) {
      loadDistrictData(filters.region);
    } else {
      // region이 빈 값이면 district 데이터를 초기화
      setDistrictData([]);
      setSubdistrictData([]);
    }
  }, [filters.region]);
  
  useEffect(() => {
    if (filters.districts.length > 0) {
      loadSubdistrictData(filters.districts);
    } else {
      // districts가 비어있으면 subdistrict 데이터를 초기화
      setSubdistrictData([]);
    }
  }, [filters.districts]);
  
  const loadRegionData = async () => {
    try {
      const response = await fetch('/region_rows.csv');
      const text = await response.text();
      const rows = text.split('\n').slice(1); // 헤더 제외
      const data = rows.map(row => {
        const [region_number, region_name] = row.trim().split(',');
        return { 
          region_number: region_number?.trim(), 
          region_name: region_name?.trim() 
        };
      }).filter(item => item.region_number);
      setRegionData(data);
    } catch (err) {
    }
  };
  
  const loadDistrictData = async (regionNumber) => {
    try {
      const response = await fetch('/district_rows.csv');
      const text = await response.text();
      const rows = text.split('\n').slice(1);
      const data = rows.map(row => {
        const [district_number, district_name, region_number] = row.trim().split(',');
        return { 
          district_number: district_number?.trim(), 
          district_name: district_name?.trim(), 
          region_number: region_number?.trim() 
        };
      }).filter(item => item.region_number && item.region_number === regionNumber);
      setDistrictData(data);
    } catch (err) {
    }
  };
  
  const loadSubdistrictData = async (districtNumbers) => {
    try {
      const response = await fetch('/subdistrict_rows.csv');
      const text = await response.text();
      const rows = text.split('\n').slice(1);
      const data = rows.map(row => {
        const [subdistrict_number, subdistrict_name, district_number] = row.trim().split(',');
        return { 
          subdistrict_number: subdistrict_number?.trim(), 
          subdistrict_name: subdistrict_name?.trim(), 
          district_number: district_number?.trim() 
        };
      }).filter(item => {
        return item.district_number && districtNumbers.includes(item.district_number);
      });
      setSubdistrictData(data);
    } catch (err) {
    }
  };
  
  // 필터 변경 핸들러
  const handleFilterChange = (filterName, value) => {
    if (filterName === 'region') {
      // region이 변경되면 districts와 subdistricts도 초기화
      setFilters(prev => ({
        ...prev,
        region: value,
        districts: [],
        subdistricts: []
      }));
    } else if (filterName === 'districts') {
      // districts가 변경되면 subdistricts도 초기화
      setFilters(prev => ({
        ...prev,
        districts: value,
        subdistricts: []
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterName]: value
      }));
    }
  };
  
  // 검색 실행
  const handleSearch = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await api.filterApartments(filters);
      
      if (error) {
        // error 객체가 { message, retry, status, data } 형태인 경우 처리
        if (typeof error === 'object' && error.message) {
          setError(error);
        } else if (typeof error === 'string') {
          setError({ message: error, retry: false });
        } else {
          setError({ message: '검색 중 오류가 발생했습니다.', retry: false });
        }
        setResults([]);
      } else {
        // API 응답이 { success, count, data } 형식인 경우 처리
        if (data && data.success && Array.isArray(data.data)) {
          setResults(data.data);
        } else if (Array.isArray(data)) {
          setResults(data);
        } else {
          setResults([]);
        }
      }
    } catch (err) {
      setError({ message: '검색 중 오류가 발생했습니다.', retry: false });
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 필터 초기화
  const handleReset = () => {
    setFilters({
      region: '',
      districts: [],
      subdistricts: [],
      households: { min: null, max: null },
      area: { min: null, max: null },
      price: { min: null, max: null },
      subwayTime: null,
      priceChange: '',
      sortBy: 'sale_price',
      sortOrder: 'desc',
      limit: 10
    });
    setResults([]);
    setError(null);
  };
  
  return (
    <div className="bg-[#FFFEF6] min-h-screen">
      <div className="container mx-auto px-4 py-8">
      <ApartmentSearchForm
        filters={filters}
        regionData={regionData}
        districtData={districtData}
        subdistrictData={subdistrictData}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        onReset={handleReset}
      />
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg my-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p>{typeof error === 'string' ? error : error.message}</p>
              {error.retry && (
                <p className="text-sm mt-1">
                  데이터를 불러오는데 시간이 걸렸습니다. 다시 시도하시면 결과를 확인하실 수 있습니다.
                </p>
              )}
            </div>
            {error.retry && (
              <button
                onClick={handleSearch}
                className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                disabled={isLoading}
              >
                다시 시도
              </button>
            )}
          </div>
        </div>
      )}
      
      <ApartmentSearchResults
        results={results}
        isLoading={isLoading}
        filters={filters}
      />
      </div>
    </div>
  );
}